
import { EventEmitter } from 'events';

import EMFBoundingBox from '../EMFBoundingBox';
import EMFDebugger from '../EMFDebugger';
import Events from '../Events';
import MaterialTypes from '../MaterialTypes';

import EMFMesh from '../gh/geometry/EMFMesh';

import SBPBin from '../sbp/SBPBin';
import SBPVisualizer from '../sbp/SBPVisualizer';

import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import MaterialManager from '../manager/MaterialManager';
import PaintManager from '../manager/PaintManager';
import Grid from '../misc/Grid';
import SBPCNC from '../sbp/SBPCNC';

const CNCVisibledKey = '__VUILD_CNC_VISIBLED__';

export default class BaseController extends EventEmitter {
  constructor (baseUrl, canvas, theme, options = { unit: 0.05 }) {
    super();

    this.baseUrl = baseUrl;
    this.theme = theme;
    this.options = options || {};

    const { materials, paints } = options;
    this.materialManager = new MaterialManager(materials, baseUrl);
    this.paintManager = new PaintManager(paints);

    this.setupScene(canvas);
    this.setupBackground(this.scene);
    this.setupTextures();
    this.resize();

    this.tick();

    global.debugger = new EMFDebugger();
    this.scene.add(global.debugger.container);
  }

  async setupAsync () {
    await this.setupCNCGeometry();
  }

  get theme () {
    return this._theme;
  }

  set theme (t) {
    this._theme = t;

    if (this.boundingBox !== undefined) this.boundingBox.setColor(t.boundingBoxColor, t.boundingBoxAlpha);
  }

  get fileType () {
    throw new Error('Not implemented FileType getter');
  }

  destroy () {
    this.reset();
    this.resetScene();
    this.renderer.dispose();
  }

  setupScene (canvas) {
    // let r = canvas.getBoundingClientRect()
    let parent = canvas.parentElement;
    let r = parent.getBoundingClientRect();

    // const w = window.innerWidth
    // const h = window.innerHeight
    const w = r.width;
    const h = r.height;

    const renderer = new THREE.WebGLRenderer({
      canvas: canvas,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true
    });
    renderer.shadowMap.enabled = true;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(w, h);
    renderer.setClearColor(0xf0f0f0, 0);
    // renderer.setClearColor(0x000000, 1)
    renderer.gammaOutput = true;

    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    const mouse = new THREE.Vector2();
    const scene = new THREE.Scene();

    const container = new THREE.Object3D();
    const vContainer = new THREE.Object3D();

    camera.position.y = 1.0;
    camera.position.z = 1.0;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableKeys = false;
    controls.screenSpacePanning = true;
    controls.rotateSpeed = 1.0;
    controls.minDistance = 0.25;
    controls.maxDistance = 15.0;

    const misc = new THREE.Object3D();

    controls.addEventListener('change', (e) => {
      this.emit(Events.UPDATE_CONTROLS);
    });
    // controls.minPolarAngle = - Math.PI * 0.75
    // controls.maxPolarAngle = Math.PI * 0.75

    {
      // light settings
      const ambLight = new THREE.AmbientLight(0xa0a0a0);
      ambLight.position.set(0, 0, 0);
      scene.add(ambLight);

      const dirLight = new THREE.DirectionalLight(0xffffff, 0.75);
      dirLight.position.set(0, 5, -5);
      dirLight.castShadow = true;
      dirLight.shadow.camera.near = 3.0;
      dirLight.shadow.camera.far = 15;
      dirLight.shadow.camera.top = -3;
      dirLight.shadow.camera.right = 3;
      dirLight.shadow.camera.left = -3;
      dirLight.shadow.camera.bottom = 3;
      dirLight.shadow.mapSize.width = dirLight.shadow.mapSize.height = 2048;
      scene.add(dirLight);

      // const shadowHelper = new THREE.CameraHelper(dirLight.shadow.camera)
      // scene.add(shadowHelper)

      const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
      dirLight2.position.set(0, 3, 5);
      // scene.add(dirLight2)
    }

    // unit: value * 1000mm
    const gunit = this.options.unit || 1.0;
    const gdiv = this.options.divisions || 20;
    const gsize = gunit * gdiv;
    const grid = new Grid(gunit, gsize, gdiv, new THREE.Color(0xffffff), new THREE.Color(0xffffff));
    grid.renderOrder = 1e5;
    misc.add(grid);

    const planeGeometry = new THREE.PlaneGeometry(gsize, gsize, 2, 2);
    const shadowPlane = new THREE.Mesh(
      planeGeometry,
      new THREE.ShadowMaterial({
        opacity: 0.2
      })
    );
    shadowPlane.rotation.set(-Math.PI * 0.5, 0, 0);
    shadowPlane.receiveShadow = true;
    misc.add(shadowPlane);

    const whitePlane = new THREE.Mesh(
      planeGeometry,
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xd0d0d0)
      })
    );
    whitePlane.position.set(0, -0.05, 0);
    whitePlane.rotation.set(-Math.PI * 0.5, 0, 0);
    misc.add(whitePlane);

    scene.add(container);
    scene.add(vContainer);

    this.boundingBox = new EMFBoundingBox(this.theme.boundingBoxColor, this.theme.boundingBoxAlpha);
    this.boundingBox.visible = this.options.boundingBox;
    misc.add(this.boundingBox);
    scene.add(misc);

    this.renderer = renderer;
    this.isRendering = true;

    this.scene = scene;
    this.camera = camera;
    this.mouse = mouse;

    this.container = container;
    this.vContainer = vContainer;
    this.grid = grid;
    this.shadowPlane = shadowPlane;
    this.whitePlane = whitePlane;
    this.misc = misc;
    this.controls = controls;
    this.materialType = MaterialTypes.NORMAL;

    this.sbpVisualizers = [];

    this.resetCamera();
  }

  setupBackground (scene) {
    let urls = ['px.png', 'nx.png', 'py.png', 'ny.png', 'pz.png', 'nz.png'];
    let cubemap = new THREE.CubeTextureLoader().load(urls.map(url => `${this.baseUrl}static/textures/cube/room/${url}`));
    this.cubeMap = cubemap;
  }

  updateGrid () {
    if (!this.grid.contains(this.boundingBox)) {
      this.grid.expand(this.boundingBox);
      const newSize = this.grid.size;
      this.whitePlane.geometry = this.shadowPlane.geometry = new THREE.PlaneGeometry(newSize, newSize, 2, 2);
    }
  }

  tick (time) {
    requestAnimationFrame((elapsedTime) => { this.tick(elapsedTime); });
    this.controls.update();
    this.render();
  }

  setTimeline (t) {
    this.sbpVisualizers.forEach(v => {
      v.t = t;
    });
  }

  setBinVisibled (visible) {
    this.sbpVisualizers.forEach(v => {
      v.binVisibled = visible;
    });
  }

  setToolpathVisibled (visible) {
    this.sbpVisualizers.forEach(v => {
      v.lineVisibled = visible;
    });
  }

  setTabVisibled (visible) {
    this.sbpVisualizers.forEach(v => {
      v.tabVisibled = visible;
    });
  }

  setBisuVisibled (visible) {
    this.sbpVisualizers.forEach(v => {
      v.bisuVisibled = visible;
    })
  }
  // nesting labelling
  setLabelinfoVisibled (visible) {
    this.sbpVisualizers.forEach(v => {
      v.labelinfoVisibled = visible;
    })
  }

  getCNCVisibled () {
    const item = localStorage.getItem(CNCVisibledKey);
    return (item === null) ? true : item === 'true';
  }

  setCNCVisibled (visible) {
    localStorage.setItem(CNCVisibledKey, visible ? 'true' : 'false');
    this.sbpVisualizers.forEach(v => {
      v.cncVisibled = visible;
    });
  }

  render () {
    // let bb = this.boundingBox
    // bb.geometry.verticesNeedUpdate = bb.geometry.elementNeedUpdate = true

    // this.scene.background = this.cubeMap
    if (this.isRendering) {
      this.visualizers.forEach(vis => {
        vis.render(this.renderer);
      });

      this.container.traverse(object => {
        if (object instanceof EMFMesh) {
          let envMap = this.cubeMap;
          if (object.material.envMap !== envMap) {
            object.material.envMap = envMap;
            object.material.needsUpdate = true;
          }
        }
      });

      const envMap = this.cubeMap;
      this.vContainer.traverse(object => {
        if (object instanceof SBPBin || object instanceof SBPCNC) {
          object.children.forEach((child) => {
            const m = child.material;
            if (m instanceof THREE.MeshStandardMaterial && m.envMap !== envMap) {
              m.envMap = envMap;
              m.needsUpdate = true;
            } else if (child.material instanceof THREE.ShaderMaterial && m.uniforms.envMap !== undefined && m.uniforms.envMap.value !== envMap) {
              m.uniforms.envMap.value = envMap;
              m.needsUpdate = true;
            }
          });
        }
      });

      this.renderer.toneMappingExposure = 1.0;
      this.renderer.render(this.scene, this.camera); // comment out to check SBPBin texture
    }
  }

  startRendering () {
    this.isRendering = true;
  }

  stopRendering () {
    this.isRendering = false;
  }

  focus (dy = 1, dz = 0.5) {
    const bb = this.boundingBox.geometry.boundingBox;
    if (bb === null || bb === undefined) {
      return;
    }
    const center = new THREE.Vector3();
    bb.getCenter(center);
    const x = (bb.max.x + bb.min.x) * 0.5;
    this.focusTo(new THREE.Vector3(x, bb.max.y + dy, bb.max.z + dz), center);
  }

  focusTo (position, center) {
    this.controls.position0.copy(position);
    this.controls.target0.copy(center);
    this.controls.reset();
  }

  resize () {
    const el = this.renderer.domElement;
    const parent = el.parentElement;
    const r = parent.getBoundingClientRect();

    // const w = window.innerWidth
    // const h = window.innerHeight
    const w = r.width;
    const h = Math.min(r.height, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    this.sbpVisualizers.forEach(visualizer => {
      visualizer.resize(w, h);
    });

    this.setResolutions(w, h);
  }

  reset () {
    this.resetContainer();
    this.resetSbp();
  }

  resetContainer () {
    let children = this.container.children;
    for (var i = children.length - 1; i >= 0; i--) {
      this.removeObject(this.container, children[i]);
    }
  }

  resetScene () {
    while (this.scene.children.length > 0) {
      this.removeObject(this.scene, this.scene.children[0]);
    }
  }

  removeObject (container, child) {
    container.remove(child);
    if (child.geometry !== undefined) {
      child.geometry.dispose();
    }
    if (child.material !== undefined && !Array.isArray(child.material)) {
      child.material.dispose();
    }
  }

  resetCamera () {
    this.controls.reset();
  }

  get UIs () {
    const items = [this.numbers, this.sliders, this.toggles, this.valueLists, this.materialLists].flat(1);
    items.sort((a, b) => {
      if (a.nickName < b.nickName) return -1;
      if (a.nickName > b.nickName) return 1;
      return 0;
    });
    return items;
  }

  get numbers () {
    return [];
  }

  get sliders () {
    return [];
  }

  get toggles () {
    return [];
  }

  get valueLists () {
    return [];
  }

  get materialLists () {
    return [];
  }

  get exportComponents () {
    return [];
  }

  get materialThickness () {
    return 24;
  }

  update (ui) {
    this.emit(Events.UPDATE);
  }

  getUnion (meshes) {
    let summary = new THREE.Box3();
    meshes.forEach(mesh => {
      let bounds = mesh.geometry.boundingBox;
      if (bounds === null) {
        mesh.geometry.computeBoundingBox();
        bounds = mesh.geometry.boundingBox;
      }
      summary.union(bounds);
    });
    return summary;
  }

  centerize (meshes) {
    let summary = this.getUnion(meshes);
    let center = new THREE.Vector3();
    summary.getCenter(center);
    this.container.position.set(-center.x, 0, -center.z);
  }

  useTexture (id) {
    if (!(id in this.textures)) {
      const material = this.options.materials.find(m => m.id === id);
      if (material !== undefined) {
        this.textures[id] = this.createTexture(material.texture);
      } else {
        return Object.values(this.textures)[0]; // default
      }
    }
    return this.textures[id];
  }

  applyTMaterial (material) {
    // eslint-disable-next-line camelcase
    const { texture_albedo, texture_normal } = material;
    const texture = this.createTexture({
      map: texture_albedo,
      normalMap: texture_normal,
      roughness: 0.47,
      metalness: 0.0
    });
    this.sbpVisualizers.forEach((visualizer) => {
      visualizer.applyTexture(texture);
    });
  }

  createTexture (pack, scale = 0.0035) {
    const callback = (t) => {
      t.repeat.set(scale, scale);
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
    };
    return {
      map: new THREE.TextureLoader().load(pack.map, callback),
      normalMap: new THREE.TextureLoader().load(pack.normalMap, callback),
      roughnessMap: new THREE.TextureLoader().load(pack.roughnessMap, callback),
      roughness: pack.roughness,
      metalness: pack.metalness
    };
  }

  setupTextures () {
    this.textures = {
      sbs: this.createTexture({
        map: `${this.baseUrl}static/textures/sbs/albedo.png`,
        normalMap: `${this.baseUrl}static/textures/sbs/normal.png`,
        roughnessMap: `${this.baseUrl}static/textures/sbs/roughness.png`,
        roughness: 0.47,
        metalness: 0.0
      })
    };
  }

  async setupCNCGeometry () {
    const xAxisTopGeometry = await this.loadOBJ(`${this.baseUrl}static/models/x-axis-top.obj`);
    const xAxisBottomGeometry = await this.loadOBJ(`${this.baseUrl}static/models/x-axis-bottom.obj`);
    const gantryYAxisGeometry = await this.loadOBJ(`${this.baseUrl}static/models/gantry-y-axis.obj`);

    xAxisTopGeometry.computeBoundingBox();
    xAxisBottomGeometry.computeBoundingBox();
    gantryYAxisGeometry.computeBoundingBox();

    const gantrySideTopGeometry = await this.loadOBJ(`${this.baseUrl}static/models/gantry-side-top.obj`);
    const gantrySideBottomGeometry = await this.loadOBJ(`${this.baseUrl}static/models/gantry-side-bottom.obj`);
    const spindleTipGeometry = await this.loadOBJ(`${this.baseUrl}static/models/spindle-tip.obj`);
    const spindleBaseGeometry = await this.loadOBJ(`${this.baseUrl}static/models/spindle-base.obj`);
    this.cncGeometry = {
      xAxisTopGeometry,
      xAxisBottomGeometry,
      gantryYAxisGeometry,
      gantrySideTopGeometry,
      gantrySideBottomGeometry,
      spindleTipGeometry,
      spindleBaseGeometry
    };
  }

  loadOBJ (path) {
    return new Promise((resolve) => {
      const loader = new THREE.OBJLoader();
      loader.load(`${path}`, (group) => {
        const mesh = group.children[0];
        const { geometry } = mesh;
        resolve(geometry);
      });
    });
  }

  parse (ghx, report = true) {
  }

  load (path, report, onLoad, onProgress, onError) {
    throw new Error('Not implemented');
  }

  worldToScreen (world) {
    let projected = world.project(this.camera);
    let rect = this.renderer.domElement.getBoundingClientRect();
    const sx = rect.top + rect.width / 2 * (projected.x + 1.0);
    const sy = rect.left + rect.height / 2 * (-projected.y + 1.0);
    return {
      x: sx,
      y: sy
    };
  }

  extractBin (sbp) {
    let lines = sbp.split('\n');

    let width = 0;
    let depth = 0;
    let thickness = 0;
    let count = 0;
    let millSize = 6.35;
    let xoffset = 0;
    let yoffset = 0;
    let cutTime = 0;

    lines.forEach(line => {
      if (line.includes('EMARF_MATERIAL_WIDTH')) {
        width = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_MATERIAL_DEPTH')) {
        depth = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_MATERIAL_THICKNESS')) {
        thickness = Number(line.split(':')[1]);
      } else if (line.includes('PARTS_NUM')) {
        count = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_MILL_SIZE')) {
        millSize = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_MATERIAL_XOFFSET')) {
        xoffset = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_MATERIAL_YOFFSET')) {
        yoffset = Number(line.split(':')[1]);
      } else if (line.includes('EMARF_CUT_TIME')) {
        cutTime = Number(line.split(':')[1]);
      }
    });

    return {
      width,
      depth,
      thickness,
      count,
      millSize,
      xoffset,
      yoffset,
      cutTime,
    };
  }

  getFiles (files, key) {
    return files.filter(file => file.name.includes(key)).sort((f0, f1) => {
      if (f0.name < f1.name) { return -1; } else { return 1; }
    });
  }

  addSbp (files, sbpColor, labellingData, sheetLabellingData) {
    const screwFiles = this.getFiles(files, 'screw');
    const cutFiles = this.getFiles(files, 'cut');
    const vbitFiles = this.getFiles(files, 'vbit');

    if (screwFiles.length !== cutFiles.length) {
      throw new Error('# of screws are not equal to # of cuts');
    }

    const count = screwFiles.length;
    const interval = 200;

    // const row = Math.ceil(Math.sqrt(count));
    const row = 5;

    // xy size constraint for SBPCNC.
    let wMax = 1820;
    let dMax = 910;

    const data = Array.from(Array(count).keys()).map((i) => {
      const screw = screwFiles[i];
      const cut = cutFiles[i];
      const bin = this.extractBin(cut.sbp);
      wMax = Math.max(wMax, bin.width);
      dMax = Math.max(dMax, bin.depth);

      const vbit = vbitFiles.find(file => {
        return file.name === `vbit_${String(i).padStart(3, '0')}.sbp`
      });

      return { screw, cut, bin, vbit };
    });

    data.sort((a, b) => {
      const wa = a.bin.width;
      const wb = b.bin.width;
      if (wa > wb) return -1;
      if (wa < wb) return 1;
      return 0;
    });

    let cncExtraOffsetX = 0;
    let cncExtraOffsetY = 0;
    if (data.length > 0) {
      cncExtraOffsetX = data[0].bin.xoffset;
      cncExtraOffsetY = data[0].bin.yoffset;
    }

    const cncOffset = new THREE.Vector3(200 + cncExtraOffsetX, 400 + cncExtraOffsetY, 0);
    let xMax = 0;
    let yMax = 0;
    wMax += cncOffset.x;
    dMax += cncOffset.y;

    let sheetCounter = 0
    const visibled = this.getCNCVisibled();
    data.forEach((file, i) => {
      const { screw, cut, bin, vbit } = file;

      const offset = new THREE.Vector3(
        (i % row) * (wMax + interval),
        Math.floor(i / row) * (dMax + interval),
        0
      );
      xMax = Math.max(xMax, offset.x + wMax);
      yMax = Math.max(yMax, offset.y + dMax);

      this.millSize = bin.millSize;
      this.width = bin.width;
      this.depth = bin.depth;

      let sheetLabel = {};
      if (Array.isArray(sheetLabellingData)) {
        const found = sheetLabellingData.find(item => item.sheet === sheetCounter)
        if (found) {
          sheetLabel = found
        }
      }
      sheetCounter += 1;

      const name = cut.name;
      const visualizer = new SBPVisualizer(bin, name, sheetLabel);
      const screwSegment = visualizer.parse(screw.sbp, true);
      const cutSegment = visualizer.parse(cut.sbp, false);

      let vbitSegment = null;
      if (vbit) {
        vbitSegment = visualizer.parse(vbit.sbp);
      }

      const { materialId, paintId } = cut;
      const texture = materialId !== undefined ? this.useTexture(materialId) : Object.values(this.textures)[0];
      const color = paintId !== undefined ? new THREE.Color(this.paintManager.use(paintId)) : new THREE.Color(0xffffff);
      visualizer.setup({
        renderer: this.renderer,
        bin,
        screwSegment,        
        cutSegment,
        vbitSegment,
        texture,
        color,
        cncGeometry: this.cncGeometry,
        cncOffset
      }, sbpColor, labellingData);
      visualizer.position.add(offset);
      visualizer.cncVisibled = visibled;
      this.vContainer.add(visualizer);
      this.sbpVisualizers.push(visualizer);


      /*
      let buffer = binMesh.depthBuffer
      let quad = new THREE.Mesh(
        new THREE.PlaneGeometry(buffer.width, buffer.height),
        new THREE.MeshBasicMaterial({
          // color: new THREE.Color(0xff0000),
          map: buffer.texture,
          side: THREE.DoubleSide
        })
      )
      this.vContainer.add(quad)
      */
    });

    const inv = 0.001;
    this.vContainer.scale.set(inv, inv, inv);
    this.vContainer.position.set(-xMax * 0.5 * inv, inv, yMax * 0.5 * inv);
    this.vContainer.rotation.set(-Math.PI * 0.5, 0, 0);

    // Resize visualizers
    this.resize();

    if (this.sbpVisualizers.length > 0) {
      const baseSbpVisualizer = this.sbpVisualizers[0];
      const bb = baseSbpVisualizer.boardBoundingBox;
      const sx = (bb.max.x - bb.min.x) * 1e-3;
      const sy = (bb.max.y - bb.min.y) * 1e-3;
      this.focusTo(new THREE.Vector3(-sx * 0.5, 1, sy * 0.5 + 0.5), new THREE.Vector3(-sx * 0.5, 0, 0));
      this.setBinVisibled(false); // set OFF as default to accelerate loading
      this.setTabVisibled(false); // set OFF as default
      this.setBisuVisibled(false); // set OFF as default
      this.setLabelinfoVisibled(false);
    }

    // return visualizer
    return this.sbpVisualizers;
  }

  resetSbp () {
    let children = this.vContainer.children;
    for (var i = children.length - 1; i >= 0; i--) {
      this.vContainer.remove(children[i]);
    }
    this.sbpVisualizers = [];
  }

  hasSbp () {
    return this.vContainer.children.length > 0;
  }

  showSbp () {
    this.vContainer.visible = true;
    this.container.visible = this.boundingBox.visible = false;
  }

  hideSbp () {
    this.vContainer.visible = false;
    this.container.visible = true;
    this.boundingBox.visible = this.options.boundingBox;
  }

  get visualizers () {
    return this.sbpVisualizers;
  }

  setResolutions (w, h) {
    this.container.traverse((object) => {
      this.setResolution(object, w, h);
    });
    this.vContainer.traverse((object) => {
      this.setResolution(object, w, h);
    });
    this.setResolution(this.boundingBox, w, h);
  }

  setResolution (object, w, h) {
    if (object.setResolution) {
      // console.log(object, w, h)
      object.setResolution(w, h);
    }
  }

  captureBase64 (quality = 0.5) {
    return this.renderer.domElement.toDataURL('image/jpeg', quality);
  }

  captureBlob (quality = 0.5) {
    let base64 = this.captureBase64(quality);
    let bin = atob(base64.replace(/^.*,/, ''));
    let buffer = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) {
      buffer[i] = bin.charCodeAt(i);
    }

    let blob;
    try {
      blob = new Blob([buffer.buffer], {
        type: 'image/png'
      });
    } catch (e) {
      return false;
    }
    return blob;
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/controllers/BaseController.js